import {
  Button,
  Checkbox,
  Flex,
  message,
  Modal,
  Radio,
  RadioChangeEvent,
  Select,
  Space,
  Tooltip,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SingleShipmentDataExtended } from '@/apis/types';
import {
  CustomButton,
  CustomShipmentText,
  CustomDatePicker,
  CustomTimePicker,
} from '@/components';
import '../Shipment.scss';
import { DownOutlined } from '@ant-design/icons';
import { CURATED_TIMEZONES } from '../../../common/static_data';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  MarkAsDelivered,
  shipmentMassActions,
  MassActionType,
} from '@/apis/shipment';
import { ApiResponse } from '@/apis/types';
import { Spin } from 'antd';
import {
  getUserTimeZoneValue,
  combineDateAndTime,
  parseShipmentDayjs,
} from '@/common/utils/utils';
import { useToken } from '@/common/helper';
import { getTimeZoneAbbreviation } from '@/common/utils/getFormattedTimeUtils';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { splitCalendarScopedFilters } from '../ShipmentToolbar/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ShipmentMassActionsPayload {
  action: MassActionType;
  shipment_ids?: string[];
  select_all?: boolean;
  exclude_shipment_ids?: string[];
  filters?: Record<string, unknown>;
  start?: string;
  end?: string;
  view_by?: string;
}

interface DeliveryModel {
  delivery_date_option: string | null;
  fallback_option?: string;
  custom_delivery_date?: string;
  fallback_custom_date?: string;
  timezone?: string;
  shipment_id?: string;
  shipment_ids?: string[];
  action?: string;
  select_all?: boolean;
  exclude_shipment_ids?: string[];
  filters?: Record<string, unknown>;
  start?: string;
  end?: string;
  view_by?: string;
  picked_up_by_customer?: boolean;
}

interface ReferenceIdModal {
  total_without: number;
  shipments_without_delivery_date: string[];
}

interface MarkAsDeliveredModalProps {
  shipmentData: SingleShipmentDataExtended & { shipment_status?: string };
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  type: 'single' | 'multiple';
  handleConfirmDelivered?: (params: DeliveryModel) => void;
  selectedIds?: string[];
  selectAllAcrossFilters?: boolean;
  excludedIds?: string[];
  filtersPayload?: Record<string, unknown>;
  appliedSearchQuery?: string;
}

const MarkAsDeliveredModal: React.FC<MarkAsDeliveredModalProps> = ({
  shipmentData,
  open,
  onCancel,
  onSuccess,
  type,
  handleConfirmDelivered,
  selectedIds,
  selectAllAcrossFilters = false,
  excludedIds = [],
  filtersPayload = {},
  appliedSearchQuery = '',
}) => {
  const [referenceIdModal, setReferenceIdModal] =
    useState<ReferenceIdModal | null>(null);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = useState<dayjs.Dayjs | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [fallbackOption, setFallbackOption] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const { formatDateTime } = useUserDateTimeFormat();

  const [checked, setChecked] = useState(false);
  const onChange = (e: { target: { checked: boolean } }) => {
    setChecked(e.target.checked);
  };

  const userTimeZone = getUserTimeZoneValue(decodedToken);
  // CustomDatePicker's default 'ddd, MMM D' and update according to user preferred date format.
  const datePickerFormat =
    decodedToken?.date_format === 'DD/MM/YYYY' ? 'ddd, D MMM' : 'ddd, MMM D';
  const [timeZone, setTimeZone] = useState<string>(
    userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const allTimezones = useMemo(() => moment.tz.names(), []);

  const zonesToShow = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return CURATED_TIMEZONES;
    return allTimezones.filter((z) => z.toLowerCase().includes(term));
  }, [searchTerm, allTimezones]);

  const fetchReferenceIds = useCallback(async (): Promise<void> => {
    if (type !== 'multiple') return;
    if (!selectAllAcrossFilters && !selectedIds?.length) return;

    const { filters, start, end, view_by } =
      splitCalendarScopedFilters(filtersPayload);
    const payload: ShipmentMassActionsPayload = selectAllAcrossFilters
      ? {
          action: 'mark_as_delivered_preflight' as MassActionType,
          select_all: true,
          exclude_shipment_ids: excludedIds,
          shipment_ids: selectedIds,
          filters: {
            ...filters,
            search: (appliedSearchQuery ?? '').trim(),
          },
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
          ...(view_by ? { view_by } : {}),
        }
      : {
          action: 'mark_as_delivered_preflight' as MassActionType,
          shipment_ids: selectedIds,
        };

    try {
      setIsLoading(true);
      const response: ApiResponse = await shipmentMassActions(null, payload);

      if (response?.data?.data?.is_success && response?.data?.data?.data) {
        setReferenceIdModal(response.data.data.data as ReferenceIdModal);
      } else {
        message.error(
          response?.data?.data?.message || 'Failed to fetch reference IDs'
        );
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Something went wrong';
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [
    type,
    selectedIds,
    selectAllAcrossFilters,
    excludedIds,
    filtersPayload,
    appliedSearchQuery,
  ]);

  useEffect(() => {
    if (type === 'multiple') {
      fetchReferenceIds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleTzSearch = (val: string): void => setSearchTerm(val || '');
  const handleDropdownVisibleChange = (isOpen: boolean): void => {
    if (!isOpen) setSearchTerm('');
  };

  const resetDateFields = (): void => {
    setSelectedDate(null);
    setSelectedTime(null);
    setTimeZone(
      userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
    );
  };

  const handleValueChange = (e: RadioChangeEvent): void => {
    setFallbackOption('');
    resetDateFields();
    setSelectedOption(e.target.value);
  };

  const handleFallBackOption = (e: RadioChangeEvent): void => {
    resetDateFields();
    setFallbackOption(e.target.value);
  };

  const formatDateWithTZ = useCallback(
    (
      inputDate: Date | number | string,
      tz?: string,
      convertToTimeZone: boolean = false
    ): string => {
      let date: Date;
      if (typeof inputDate === 'number') {
        date = new Date(inputDate);
      } else if (typeof inputDate === 'string') {
        const parsed = parseShipmentDayjs(inputDate, decodedToken?.date_format);
        date = parsed ? parsed.toDate() : new Date(inputDate);
      } else {
        date = inputDate;
      }

      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      const resolvedTimeZone =
        tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

      const dateOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        ...(convertToTimeZone && { timeZone: resolvedTimeZone }),
      };

      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...(convertToTimeZone && { timeZone: resolvedTimeZone }),
      };

      // getTimeZoneAbbreviation re-parses this string via `new Date(...)`,
      // which only reliably round-trips through the native en-US locale
      // format — keep it separate from the user-facing display date below.
      const enUsDatePart = date.toLocaleDateString('en-US', dateOptions);
      const timePart = date.toLocaleTimeString('en-US', timeOptions);
      const tzAbbr = getTimeZoneAbbreviation(enUsDatePart, tz);

      const displayDate = convertToTimeZone
        ? dayjs(date).tz(resolvedTimeZone)
        : dayjs(date);
      const datePart = formatDateTime(displayDate, { includeTime: false });

      return `${datePart} at ${timePart} ${tzAbbr}`;
    },
    [formatDateTime, decodedToken?.date_format]
  );

  const handleConfirm = async (): Promise<void> => {
    const model: DeliveryModel = {
      delivery_date_option: selectedOption || null,
      picked_up_by_customer: checked,
    };

    if (type === 'multiple' && selectedOption === 'carrier_scan') {
      model.fallback_option = fallbackOption || 'now';
    }

    if (selectedOption === 'custom') {
      const dateInUTC = combineDateAndTime(selectedDate, selectedTime);
      if (!dateInUTC) {
        message.error('Please select a valid delivery date and time');
        return;
      }
      model.custom_delivery_date = dateInUTC;
      model.timezone = timeZone;
    }

    if (fallbackOption === 'custom') {
      const dateInUTC = combineDateAndTime(selectedDate, selectedTime);
      if (!dateInUTC) {
        message.error('Please select a valid delivery date and time');
        return;
      }
      model.fallback_custom_date = dateInUTC;
      model.timezone = timeZone;
    }

    if (type === 'multiple') {
      model.action = 'mark_as_delivered';

      if (selectAllAcrossFilters) {
        const { filters, start, end, view_by } =
          splitCalendarScopedFilters(filtersPayload);
        model.select_all = true;
        model.exclude_shipment_ids = excludedIds;
        model.filters = {
          ...filters,
          search: (appliedSearchQuery ?? '').trim(),
        };
        if (start) model.start = start;
        if (end) model.end = end;
        if (view_by) model.view_by = view_by;
      } else {
        model.shipment_ids = selectedIds;
      }
    }

    if (type === 'single') {
      if (shipmentData?.shipment_status?.toLowerCase() === 'delivered') {
        message.error('Shipment is already delivered');
        onCancel();
        return;
      }

      model.shipment_id = shipmentData?.id || '';

      try {
        setIsLoading(true);
        const res: ApiResponse = await MarkAsDelivered(null, model);
        const data = res?.data ? res.data : null;

        if (data) {
          if (data?.data?.is_success) {
            onSuccess?.();
            onCancel();
            message.success(data?.data?.message);
          } else {
            message.error(data?.data?.message);
          }
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to mark as delivered';
        message.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    } else {
      handleConfirmDelivered?.(model);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      className='delivered-modal-wrapper'
      footer={
        <>
          <Button type='text' onClick={onCancel} className='footer-btns'>
            {'Cancel'}
          </Button>
          <CustomButton
            loader={isLoading ? <Spin /> : null}
            isFormValid={
              !selectedOption ||
              isLoading ||
              ((selectedOption === 'custom' || fallbackOption === 'custom') &&
                (!selectedDate || !selectedTime || !timeZone))
            }
            text={'Mark as delivered'}
            bgColor='#000'
            color='#fff'
            onClick={handleConfirm}
            className='footer-btns save'
          />
        </>
      }
      centered
      width={542}
      title={
        <CustomShipmentText
          fontSize='24px'
          fontWeight={700}
          lineHeight='32px'
          text={'Mark shipment as delivered'}
        />
      }
      styles={{
        body: {
          margin: '20px 0',
          borderRadius: 16,
        },
      }}
    >
      <Flex gap={20} vertical>
        <CustomShipmentText
          fontSize='16px'
          lineHeight='24px'
          text={
            'Marking as delivered will update the shipment status and dismiss all open alerts for this shipment.'
          }
        />
        <Flex vertical>
          <CustomShipmentText
            fontSize='14px'
            fontWeight={600}
            text={'Delivery timestamp'}
          />
          <Radio.Group
            buttonStyle={'solid'}
            onChange={(e) => handleValueChange(e)}
          >
            <Space size={0} direction='vertical'>
              {shipmentData?.actual_delivery_date && type === 'single' && (
                <Radio className='r-option custom-radio' value={'carrier_scan'}>
                  <p>{shipmentData?.courier?.title} delivered scan</p>
                  <div className='spacer-dot'></div>
                  <p className='fw-400'>
                    {formatDateWithTZ(
                      shipmentData?.actual_delivery_date,
                      userTimeZone,
                      false
                    )}
                  </p>
                </Radio>
              )}

              {type === 'multiple' && (
                <>
                  <Radio className='r-option custom-radio' value='carrier_scan'>
                    <p>Delivered scan when available</p>
                    <p
                      className='fw-400'
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      ({referenceIdModal?.total_without} not available,&nbsp;
                      <Tooltip
                        title={referenceIdModal?.shipments_without_delivery_date?.join(
                          ', '
                        )}
                      >
                        <u style={{ color: 'var(--text-primary)' }}>view</u>
                      </Tooltip>
                      )
                    </p>
                  </Radio>

                  {selectedOption === 'carrier_scan' && (
                    <>
                      <CustomShipmentText
                        text={'If not available, use below'}
                        lineHeight='20px'
                        color={'#5E5E5E'}
                      />
                      <Flex vertical style={{ marginLeft: '24px' }}>
                        <Radio.Group onChange={(e) => handleFallBackOption(e)}>
                          <Space size={0} direction='vertical'>
                            <Radio
                              className='r-option custom-radio'
                              value='now'
                            >
                              <p>Now</p>
                              <div className='spacer-dot'></div>
                              <p className='fw-400'>
                                {formatDateWithTZ(
                                  new Date(),
                                  userTimeZone,
                                  true
                                )}
                              </p>
                            </Radio>
                            <Radio
                              className='r-option custom-radio'
                              value='custom'
                            >
                              <p>Custom date & time</p>
                            </Radio>
                          </Space>
                        </Radio.Group>
                      </Flex>
                    </>
                  )}
                </>
              )}

              <Radio className='r-option custom-radio' value={'now'}>
                <p>Now</p>
                <div className='spacer-dot'></div>
                <p className='fw-400'>
                  {formatDateWithTZ(new Date(), userTimeZone, true)}
                </p>
              </Radio>

              <Radio className='r-option custom-radio' value={'custom'}>
                <p>Custom date & time</p>
              </Radio>
            </Space>
          </Radio.Group>
        </Flex>
        {(selectedOption === 'custom' || fallbackOption === 'custom') && (
          <Flex vertical gap={4}>
            <CustomShipmentText
              fontSize='14px'
              fontWeight={600}
              text={'Delivered at'}
            />
            <div className='custom-date-wrapper'>
              <CustomDatePicker
                value={selectedDate}
                onChange={(date) => {
                  if (!Array.isArray(date) && date) {
                    setSelectedDate(date);
                  }
                }}
                timeZone={timeZone || userTimeZone}
                dateFormat={datePickerFormat}
                className='custom-picker delivered-modal-inputs'
                showToday={false}
                suffixIcon={<DownOutlined style={{ fontSize: 12 }} />}
              />

              <div
                style={{ minWidth: '20px', width: '20px', textAlign: 'center' }}
              >
                <CustomShipmentText text={'at'} />
              </div>

              <CustomTimePicker
                value={selectedTime}
                onChange={setSelectedTime}
                timeZone={timeZone || userTimeZone}
                className='custom-picker delivered-modal-inputs'
                showNow={false}
              />

              <Select
                showSearch
                value={timeZone}
                onChange={(value: string) => setTimeZone(value)}
                onSearch={handleTzSearch}
                filterOption={false}
                onDropdownVisibleChange={handleDropdownVisibleChange}
                placeholder='Timezone'
                className='custom-dd delivered-modal-inputs'
              >
                {zonesToShow.map((zone) => (
                  <Select.Option key={zone} value={zone}>
                    {zone}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Flex>
        )}
        <Checkbox checked={checked} onChange={(e) => onChange(e)}>
          <p className='zl-heading-14 font-700'>
            Shipment was Picked Up by Customer
          </p>
        </Checkbox>
      </Flex>
    </Modal>
  );
};

export default MarkAsDeliveredModal;
